import './index.scss';
import ConfigurableInfoBlock, {ConfigurableInfoBlockFeedbackIconPositions} from "../../../../../../components/app-specific/configurable-info-block";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, Skeleton, Tooltip, Typography} from "@mui/material";
import {ReactComponent as EnterIcon} from "../../../../../../../assets/images/products/enter.svg";
import ProductChipsCodeContent from "../../../../../../components/app-specific/chips-contents/product-code-content";
import {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {Col, Row} from "reactstrap";
import Form from "../../../../../../components/base/form";
import Button from "../../../../../../components/base/button/button";
import Dialog from "../../../../../../components/base/dialog";
import {ReactSortable} from "react-sortablejs";
import Sortable from "sortablejs";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {MarkProductForCacheSectionNames} from "../../../../../../../core/constants/enums";
import Api from "../../../../../../../core/services/api/api";
import CopyToClipboard from "../../../../../../components/base/copy-to-clipboard";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ContentCopy, OpenInNew} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import ToastService from "../../../../../../../core/services/toast";
import Regexes from "../../../../../../../core/models/regexes";
import Utils from "../../../../../../../core/services/utils/utils";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";

const initialEditCodeDialogState = {
    open: false,
    codeGroup: null,
    code: null,
    onEdit: () => void 0,
    onClose: () => void 0,
}

const createSortableId = (id) => `code-group-${id}`;

const ProductInformationCodesViewCodeGroups = ({productId, loading: loadingProps, setIsCacheNeeded, splitter}) => {
    const [codeGroups, setCodeGroups] = useState([]);
    const [loadingCodes, setLoadingCodes] = useState(true);
    const [removingIds, setRemovingIds] = useState([]);
    const [settingAsDefault, setSettingAsDefault] = useState({});
    const [editCodeDialogState, setEditCodeDialogState] = useState(initialEditCodeDialogState);
    const [draggingCodes, setDraggingCodes] = useState([]);
    const [draggingFromCodeGroup, setDraggingFromCodeGroup] = useState(null);
    const dispatch = useDispatch();
    const queue = useRef(null);
    const partnerInformation = usePartnerInformation();
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingCodes, [loadingProps, loadingCodes])


    /**
     * Fetches the code groups from the server.
     * @type {(function(): void)}
     */
    const getCodeGroups = useCallback(async () => {
        setLoadingCodes(true);
        const [codeTypesResponse, codesResponse] = await Promise.all([
            Api.listPartCodeCategories(),
            Api.listPartCodeValuesForEdit(productId),
        ]);
        if (!isMounted())
            return;
        setLoadingCodes(false);
        if (codeTypesResponse?.resultFlag && codesResponse?.resultFlag) {
            const categories = codeTypesResponse.data?.map(category => ({
                ...category,
                codes: codesResponse.data?.filter(code => code.partCodeCategoryId === category.id) ?? [],
            })) ?? [];
            setCodeGroups(categories);
        }
    }, [isMounted, productId])

    /**
     * As soon as the component is mounted:
     * - Fetches the code groups.
     */
    useEffect(() => {
        void getCodeGroups();
    }, [getCodeGroups])

    /**
     * Checks if the keywords contain any errors and returns the error message if they do.
     * @type {function(*): [string|null,*|string]}
     */
    const keywordsError = useCallback((keywords) => {
        /**
         * Checks if the string contains special characters
         * @type {function(string=): boolean}
         */
        const containsSpecialCharacters = (str) => {
            str = str?.trim() ?? "";
            return str.match(Regexes.noSpecialCharacters) !== null;
        };

        const errors = [];
        let fixedKeywords = keywords?.trim() ?? "";

        const skipSpecialCharacterError = Utils.skipSpecialCharacterError(partnerInformation?.id);
        if (!skipSpecialCharacterError && containsSpecialCharacters(keywords)) {
            errors.push("The provided code contain special characters and cannot be created.");
            fixedKeywords = fixedKeywords.replaceAll(Regexes.specialCharacters, '');
        }

        const errorString = errors.join(' ').trim();
        return [!!errorString ? errorString : null, fixedKeywords];
    }, [partnerInformation?.id])

    /**
     * Creates a product code.
     * @type {function(*, *): {data: {isDefault: boolean, code, id: number}, resultFlag: boolean}}
     */
    const createCode = useCallback(async (codeGroup, codeString) => {
        const codes = (codeString ?? '').split(splitter).map((token) => Utils.normalizePartCode(token)).filter(Boolean);
        const newCodes = [];
        for (const code of codes) {
            if (codeGroup.codes.some((existing) => Utils.normalizePartCode(existing.code) === code) || newCodes.includes(code)) {
                continue;
            }
            newCodes.push(code);
        }
        if (!newCodes) {
            return {
                resultFlag: false,
                message: 'The provided codes already exist in the group.',
            }
        }

        const erroneousCodes = newCodes.filter(code => {
            const [error] = keywordsError(code);
            return !!error;
        })
        const correctCodes = newCodes.filter(e => !erroneousCodes.includes(e));
        if (erroneousCodes.length) {
            ToastService.warning({
                title: `Special Characters`,
                message: `
                <div>
                    The following codes contain special characters and cannot be created:
                    <ul>
                        ${erroneousCodes.map(e => (`<li>${e}</li>`)).join('')}
                    </ul>
                </div>
                `,
            })
            if (!correctCodes.length) {
                return {
                    resultFlag: false,
                    message: 'The provided codes contain special characters and cannot be created.',
                }
            }
        }
        let success = false;
        for (const newCode of correctCodes) {
            const response = await Api.createPartCodeValue({
                code: newCode,
                partId: productId,
                partCodeCategoryId: codeGroup.id,
            })
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                success = true;
                setCodeGroups(prevState => prevState.map(group => {
                    if (group.id === codeGroup.id) {
                        return {
                            ...group,
                            codes: [
                                ...group.codes,
                                response.data,
                            ],
                        }
                    }
                    return group;
                }))
            }
        }
        if (success) {
            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.code]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return success ? {resultFlag: true} : {resultFlag: false, message: "Error while creating the provided code(s)."}
    }, [isMounted, productId, setIsCacheNeeded, splitter, keywordsError])

    /**
     * Removes a product code.
     * @type {(function(*, *): void)}
     */
    const removeCode = useCallback((codeGroup, code) => {
        return new Promise(r => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
                open: true,
                data: null,
                removedEntryTitle: `code: "${code.code}" from the group: "${codeGroup.title}"`,
                onClose: async (confirmed) => {
                    if (!confirmed) {
                        r(false);
                        return;
                    }
                    setRemovingIds([...removingIds, code.id]);
                    const response = await Api.deletePartCodeValue(code.id);
                    if (!isMounted()) {
                        r(false);
                        return;
                    }
                    setRemovingIds(prevState => prevState.filter(id => id !== code.id));
                    if (response?.resultFlag) {
                        setCodeGroups(codeGroups.map(group => {
                            if (group.id === codeGroup.id) {
                                return {
                                    ...group,
                                    codes: group.codes.filter(c => c.id !== code.id),
                                }
                            }
                            return group;
                        }))

                        Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.code]})
                            .then(response => {
                                if (response?.resultFlag) {
                                    setIsCacheNeeded?.(true)
                                }
                            });
                    }
                    r(response?.resultFlag);
                    return response?.resultFlag;
                }
            }));
        })
    }, [codeGroups, dispatch, isMounted, removingIds, productId, setIsCacheNeeded])

    /**
     * Sets a product code as default.
     * @type {(function(*, *): void)}
     */
    const setCodeAsDefault = useCallback((codeGroup, code) => {
        setSettingAsDefault(prevState => ({...prevState, [codeGroup.id]: code.id,}));
        // TODO: call api to set a product code as default.
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        setSettingAsDefault(prevState => ({
            ...prevState,
            [codeGroup.id]: undefined,
        }))
        if (response?.resultFlag) {
            setCodeGroups(codeGroups.map(group => {
                if (group.id === codeGroup.id) {
                    return {
                        ...group,
                        codes: group.codes.map(c => ({...c, isDefault: c.id === code.id})),
                    }
                }
                return group;
            }))
        }
    }, [codeGroups, isMounted])

    /**
     * Opens the edit code dialog.
     * @type {(function(*, *): void)}
     */
    const openEditCodeDialog = useCallback((codeGroup, code) => {
        setEditCodeDialogState({
            open: true,
            codeGroup: codeGroup,
            code: code,
            onEdit: (newCode) => {
                Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.code]})
                    .then(response => {
                        if (response?.resultFlag) {
                            setIsCacheNeeded?.(true)
                        }
                    });
                setCodeGroups(prevState => {
                    return prevState.map(group => {
                        if (group.id === codeGroup.id) {
                            return {
                                ...group,
                                codes: group.codes.map(c => c.id === code.id ? newCode : c),
                            }
                        }
                        return group;
                    })
                })
            },
        })
    }, [productId, setIsCacheNeeded])

    /**
     * Removes the codes from the dragging codes list.
     */
    const onCodesRemoved = useCallback((sourceGroupId) => {
        setCodeGroups(prevState => {
            return prevState.map(group => group.id === sourceGroupId
                ? {...group, codes: group.codes.filter(e => !e.remove)}
                : group
            )
        })
    }, [])

    /**
     * Adds the codes to the target group.
     * @type {(function(*, *, *): Promise<void>)}
     */
    const moveCodes = useCallback(async (sourceGroupId, targetGroupId, entries) => {
        setDraggingCodes(prevState => [...prevState, {from: sourceGroupId, to: targetGroupId}]);
        let anySuccessful = false;
        const successes = [];
        for (const entry of entries) {
            const response = await Api.updatePartCodeValue({
                id: entry.code.id,
                code: entry.code.code,
                partCodeCategoryId: targetGroupId,
                updatedProperties: [
                    'PartCodeCategoryId',
                ],
            })
            if (!isMounted())
                return
            if (response?.resultFlag) {
                anySuccessful = true;
                successes.push(entry);
            }
            // if (!response?.resultFlag) {
            //     // revert the changes for one entry if it did not work.
            //     setCodeGroups(prevState => prevState.map(group => {
            //         if (group.id === sourceGroupId) {
            //             return {...group, codes: [...group.codes, entry.code]}
            //         }
            //         if (group.id === targetGroupId) {
            //             return {...group, codes: group.codes.filter(e => e.id !== entry.code.id)}
            //         }
            //         return group;
            //     }))
            // } else {
            //     anySuccessful = true;
            // }
        }
        if (anySuccessful) {
            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.code]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setTimeout(() => {
                requestAnimationFrame(() => {
                    setCodeGroups(prevState => {
                        return prevState.map(group => {
                            if (group.id === sourceGroupId) {
                                return {...group, codes: group.codes.filter(e => !successes.some(entry => entry.code.id === e.id))}
                            }
                            if (group.id === targetGroupId) {
                                return {...group, codes: [...group.codes, ...successes.map(e => e.code)]}
                            }
                            return group;
                        })
                    })
                })
            }, 1000)
        }
        if (!isMounted())
            return;
        setDraggingCodes(prevState => prevState.filter(({from, to}) => !(from === sourceGroupId && to === targetGroupId)));
    }, [isMounted, productId, setIsCacheNeeded])

    /**
     * Updates the codes' groups based on the provided drag information.
     * @type {ReactSortable<any>['onAdd']}
     */
    const onCodesAdded = useCallback((e) => {
        const sourceGroupId = Number(e.from.id.replace('code-group-', ''));
        const targetGroupId = Number(e.to.id.replace('code-group-', ''));
        if (sourceGroupId === targetGroupId)
            return;
        const entries = [];
        if (!e.newIndicies.length && !e.oldIndicies.length) {
            entries.push({
                code: codeGroups.find(group => group.id === sourceGroupId).codes.find(c => c.id === Number(e.item.dataset.id)),
                sourceGroupId: sourceGroupId,
                targetGroupId: targetGroupId,
            })
        } else {
            for (const {multiDragElement} of e.newIndicies) {
                entries.push({
                    code: codeGroups.find(group => group.id === sourceGroupId).codes.find(e => e.id === Number(multiDragElement.dataset.id)),
                    sourceGroupId: sourceGroupId,
                    targetGroupId: targetGroupId,
                })
            }
        }
        if (!entries.length)
            return;
        for (const itemElement of e.items ?? [e.item]) {
            Sortable.utils.deselect(itemElement);
        }
        queue.current = {sourceGroupId, targetGroupId, entries}
        // move all codes
        // const entryIds = entries.map(e => e.code.id);
        // setCodeGroups(prevState => {
        //     return prevState.map(group => {
        //         if (group.id === sourceGroupId) {
        //             return {...group, codes: group.codes.map(e => entryIds.includes(e.id) ? ({...e, remove: true}) : e)}
        //         }
        //         if (group.id === targetGroupId) {
        //             const codes = entries.map(e => e.code);
        //             return {...group, codes: [...group.codes, ...codes]}
        //         }
        //         return group;
        //     })
        // })
        // void moveCodes(sourceGroupId, targetGroupId, entries);
    }, [codeGroups])


    /**
     * Indicates the code's dragging is done.
     * @type {ReactSortable<any>['onEnd']}
     */
    const onDragEnded = useCallback(() => {
        setDraggingFromCodeGroup(null);
        if (queue.current) {
            void moveCodes(queue.current.sourceGroupId, queue.current.targetGroupId, queue.current.entries);
            queue.current = null;
        }
    }, [moveCodes])

    /**
     * Indicates form which group was the dragging initiated.
     */
    const onDragStarted = useCallback((e) => {
        setDraggingFromCodeGroup(Number(e.from.id.replace('code-group-', '')));
    }, [])

    return (
        <>
            {
                loading &&
                Array.from({length: 6}).map((_, index) => (
                    <Grid xs={12} md={6} xl={6} key={`loading-group-${index}`}>
                        <div className={'part-info-paper p-2 h-100'}>
                            <Skeleton>
                                <Typography variant={'h6'} className={'text-center'}>
                                    This is a code
                                </Typography>
                            </Skeleton>
                            <div className={'d-flex flex-wrap p-2'} style={{height: 'fit-content', gap: 8}}>
                                {
                                    Array.from({length: 15}).map((_, _index) => (
                                        <Skeleton
                                            key={`loading-code-${_index}`}
                                            variant={'rounded'}
                                            width={100}
                                            height={36}
                                            sx={{transform: 'none', borderRadius: 2}}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    </Grid>
                ))
            }
            {
                !loading && !codeGroups.length &&
                <Grid xs={12}>
                    <div className={'part-info-paper p-5 d-flex justify-content-center'}>
                        <Typography variant={'body2'} className={'text-center p-5'}>
                            You do not have any code groups in the system. please create one prior to adding codes for this product.
                            <IconButton
                                component={Link}
                                to={routes.products.codeTypes}
                                size={'small'}
                                color={'primary'}
                                sx={{marginInlineStart: 0.5}}
                            >
                                <OpenInNew/>
                            </IconButton>
                        </Typography>
                    </div>
                </Grid>
            }
            {
                !loading &&
                codeGroups.map((codeGroup) => (
                    <Grid xs={12} md={6} xl={6} key={codeGroup.id}>
                        <CodeGroupCard
                            codeGroup={codeGroup}
                            draggingFromCodeGroup={draggingFromCodeGroup}
                            draggingCodes={draggingCodes}
                            settingAsDefault={settingAsDefault}
                            createCode={createCode}
                            removeCode={removeCode}
                            onDragEnded={onDragEnded}
                            onDragStarted={onDragStarted}
                            onCodesAdded={onCodesAdded}
                            onCodesRemoved={onCodesRemoved}
                            setCodeAsDefault={setCodeAsDefault}
                            openEditCodeDialog={openEditCodeDialog}
                            splitter={splitter}
                        />
                    </Grid>
                ))
            }
            <EditCodeDialog
                {...editCodeDialogState}
                onClose={() => setEditCodeDialogState(initialEditCodeDialogState)}
            />
        </>
    );
}

export default ProductInformationCodesViewCodeGroups;


const CodeGroupCard = ({
                           codeGroup,
                           settingAsDefault,
                           draggingFromCodeGroup,
                           draggingCodes,
                           createCode,
                           removeCode,
                           onDragEnded,
                           onDragStarted,
                           onCodesAdded,
                           onCodesRemoved,
                           setCodeAsDefault,
                           openEditCodeDialog,
                           splitter,
                       }) => {
    const showDropzone = useMemo(() =>
            !!draggingFromCodeGroup && draggingFromCodeGroup !== codeGroup.id,
        [draggingFromCodeGroup, codeGroup.id]);
    const showLoading = useMemo(() =>
            draggingCodes.some(({from, to}) => from === codeGroup.id || to === codeGroup.id)
        , [codeGroup.id, draggingCodes]);
    const sortableDisabled = useMemo(() =>
            draggingCodes.some(({from, to}) => from === codeGroup.id || to === codeGroup.id)
        , [codeGroup.id, draggingCodes]);
    const disabled = useMemo(() =>
            sortableDisabled || showLoading || showDropzone,
        [sortableDisabled, showLoading, showDropzone]);
    const settingAsDefaultCode = useMemo(() => settingAsDefault[codeGroup.id], [codeGroup.id, settingAsDefault]);

    /**
     * THIS IS UNDER DEVELOPEMENT.
     * @type {(function(*): void)|*}
     */
    const setList = useCallback((list) => {
        const prevCodes = codeGroup.codes;
        const newCodes = list;
        // if (prevCodes.length === newCodes.length) {
        //     return;
        // }
        const removedItems = prevCodes.filter(e => !newCodes.some(n => n.id === e.id));
        const addedItems = newCodes.filter(e => !prevCodes.some(n => n.id === e.id));
    }, [codeGroup.codes, codeGroup.title])

    return (
        <Box className={'part-info-paper p-3 h-100'}>
            <ConfigurableInfoBlock
                title={
                    <Box component={'span'} className={'d-flex flex-column'} sx={{gap: 0.5}}>
                        <Box component={'span'} className={'d-flex align-items-center'}>
                            {
                                codeGroup.title
                            }
                            {
                                codeGroup.codes?.length > 0 &&
                                <CopyToClipboard>
                                    {({copy}) =>
                                        <IconButton
                                            size={'small'}
                                            color={'paper.primary'}
                                            className={'ml-1'}
                                            onPressed={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                copy(codeGroup.codes.map(e => e.code).join(splitter));
                                            }}
                                        >
                                            <ContentCopy className={'icon small'}/>
                                        </IconButton>
                                    }
                                </CopyToClipboard>
                            }
                        </Box>
                        <Typography component={'span'} variant={'body2'}>
                            {codeGroup.description ?? ""}
                        </Typography>
                    </Box>
                }
                className={'h-100'}
                showHistoryIcon={false}
                feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                onValueChanged={(value) => createCode(codeGroup, value)}
                inputProps={{
                    placeholder: `New ${codeGroup.title}`,
                    icon: <EnterIcon className={'enter-icon'}/>,
                    removeOnCommit: true,
                    commitOnEnterPress: true,
                    commitOnIconClick: true,
                    disabled: disabled,
                    size: 'small',
                }}
            >
                <Box
                    sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', marginBlock: 2, width: '100%'}}
                    className={'flex-fill'}
                >
                    <ReactSortable
                        // multiDrag
                        id={createSortableId(codeGroup.id)}
                        setList={setList}
                        list={codeGroup.codes}
                        onEnd={onDragEnded}
                        onStart={onDragStarted}
                        onAdd={onCodesAdded}
                        // onRemove={onCodesRemoved}
                        group={{
                            name: "clone-group",
                            // pull: "clone",
                            // put: true
                        }}
                        ignore={'.ignore'}
                        chosenClass={'chosen'}
                        selectedClass={'selected-sortable'}
                        dragClass={'dragging'}
                        animation={210}
                        dragoverBubble
                        disabled={sortableDisabled}
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            padding: 10,
                            gap: 8,
                            height: 'fit-content',
                            minHeight: 200,
                            alignContent: 'flex-start',
                            justifyContent: 'flex-start',
                        }}
                    >
                        {
                            codeGroup.codes.map((code, i, array) => {
                                const numDuplicates = array.filter(e => e.code === code.code).length;
                                const duplicate = numDuplicates > 1;
                                return (
                                    <Tooltip key={code.id} title={duplicate ? `This code has ${numDuplicates - 1} duplicate${numDuplicates - 1 === 1 ? '' : 's'}` : ''}>
                                        <Box
                                            component={'span'}
                                            data-id={code.id}
                                            data-group-id={codeGroup.id}
                                            sx={(theme) => ({
                                                height: 'fit-content',
                                                ...(duplicate && {
                                                    '.chips-component.solid': {
                                                        ...(theme.palette.mode === 'dark' && {
                                                            backgroundColor: 'rgb(255 255 0 / 20%)',
                                                            borderColor: 'warning.main',
                                                        }),
                                                        ...(theme.palette.mode === 'light' && {
                                                            backgroundColor: 'warning.light',
                                                            borderColor: 'warning.light',
                                                        }),
                                                        border: '2px solid transparent',
                                                        "p": {
                                                            color: 'yellow',
                                                        },
                                                    },
                                                })
                                            })}
                                        >
                                            <Chips
                                                contentType={ChipsContentType.element}
                                                type={settingAsDefaultCode === code.id
                                                    ? ChipsTypes.loading
                                                    : ChipsTypes.remove
                                                }
                                                iconPosition={ChipsIconPosition.start}
                                                disabled={disabled || settingAsDefaultCode === code.id}
                                                onIconClick={(e) => e.stopPropagation() || e.preventDefault() || removeCode(codeGroup, code)}
                                                buttonProps={{size: 'small', color: 'paper.primary'}}
                                                content={(hovering) =>
                                                    <ProductChipsCodeContent
                                                        canCopy
                                                        code={code}
                                                        buttonProps={{size: 'small', color: 'paper.primary'}}
                                                        hovering={hovering}
                                                        settingAsDefault={settingAsDefault[codeGroup.id] === code.id}
                                                        setAsDefault={(code) => setCodeAsDefault(codeGroup, code)}
                                                        onEditCode={(code) => openEditCodeDialog(codeGroup, code)}
                                                    />
                                                }
                                            />
                                        </Box>
                                    </Tooltip>
                                )
                            })
                        }
                        <Box
                            className={'ignore'}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 0,
                                borderRadius: 1,
                                left: 0,
                                width: 'calc(100%)',
                                height: 'calc(100%)',
                                backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
                                border: `2px dashed ${theme.vars.palette.primary.main}`,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 1,
                                transition: theme.transitions.create(['opacity'], {duration: 300}),
                                ...(!showDropzone && !showLoading && {
                                    zIndex: -1,
                                    pointerEvents: 'none',
                                    opacity: 0,
                                }),
                            })}
                        >
                            <Box sx={{
                                backdropFilter: 'blur(9px)',
                                backgroundColor: 'background.tertiary',
                                borderRadius: 1,
                                padding: 1,
                                margin: 2,
                            }}>
                                <Typography
                                    variant={'caption'}
                                    color={'primary.main'}
                                    component={'div'}
                                    sx={{display: 'flex', alignItems: 'center'}}
                                    fontWeight={600}
                                    textAlign={'center'}
                                >
                                    {
                                        !showLoading && showDropzone &&
                                        "Drop the selected code(s) here to move them to this group"
                                    }
                                    {
                                        showLoading &&
                                        <>
                                            <CircularProgress size={30} sx={{marginInline: 1}}/>
                                        </>
                                    }
                                </Typography>
                            </Box>
                        </Box>
                    </ReactSortable>
                </Box>
            </ConfigurableInfoBlock>
        </Box>
    );
}


const EditCodeDialog = ({open, code, onEdit, onClose}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => (
        Yup.object().shape({
            code: Yup.string().trim().nullable().required(ValidateMessages.required),
        })
    ), [])

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        code: Utils.normalizePartCode(code?.code ?? ''),
    }), [code])

    /**
     * Updates the code.
     * @type {(function(*): Promise<void>)}
     */
    const updateCode = useCallback(async (values) => {
        const normalizedCode = Utils.normalizePartCode(values.code);
        setSubmitting(true);
        const forApi = {
            id: code.id,
            code: normalizedCode,
            updatedProperties: ["Code"],
        }
        const response = await Api.updatePartCodeValue(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onEdit({
                ...code,
                code: normalizedCode,
            });
            onClose();
        }
    }, [code, isMounted, onClose, onEdit])

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'sm'}
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Edit Code
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={updateCode}
                            id={'upsert-code-dialog-form'}
                            validate={validate}
                            initialValues={initialValues}
                            render={() => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <MuiRFF.TextField
                                                name={'code'}
                                                label={'Code'}
                                                required={required.code}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => onClose()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'upsert-code-dialog-form'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {

                                    submitting ? "Updating..." : "Update"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}

